import { useMemo, useState } from 'react';
import { isFunction, isString, noop, useDebugger } from '@chkp/mwc-common';

/**
 * Store for static arrays.
 *
 * @param {[*]} data
 * @param {string|function} [rowKey='uid']
 * @param {object} [variables] - Additional variables which are passed to the loader. Internally memoized
 * @param {function} [onVariablesChange] - Controlled variables mode
 * @param {boolean} debug
 * @returns {{rowsCount: number, getRecordByIndex: (function(number): *)}}
 */
const useArrayStore = ({ data, rowKey = 'uid', debug }) => {
	const getRowKey = useMemo(
		() =>
			isFunction(rowKey)
				? rowKey
				: isString(rowKey)
				? (r) => r?.[rowKey]
				: noop,
		[rowKey],
	);

	const rowsCount = data?.length || 0;

	const getRecordByIndex = useMemo(() => (data ? (idx) => data[idx] : noop), [
		data,
	]);

	const getRecordByKey = useMemo(() => {
		if (data) {
			const map = new Map();
			// eslint-disable-next-line no-restricted-syntax
			for (const rec of data) {
				map.set(getRowKey(rec), rec);
			}
			return (key) => map.get(key);
		}
		return noop;
	}, [data, getRowKey]);

	const api = useMemo(
		() => ({
			getRecordByIndex,
			getRecordByKey,
			rowsCount,
			rowKey: getRowKey,
		}),
		[getRecordByIndex, getRecordByKey, getRowKey, rowsCount],
	);

	if (debug) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		useDebugger('useArrayStore', {
			data,
			rowKey,
			getRecordByIndex,
			rowsCount,
			getRowKey,
			getRecordByKey,
			api,
		});
	}

	return api;
};

export { useArrayStore };
